import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, FileText, CheckCircle, AlertCircle, Trash2 } from "lucide-react";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";

interface AntrimCountyTextParserProps {
  onImportComplete?: () => void;
}

interface ParsedRecord {
  documentNumber: string;
  documentType: string;
  filingDate: string;
  party1: string;
  party2: string;
  legalDescription: string;
}

interface ImportResult {
  totalParsed: number;
  familyFiltered: number;
  bankFiltered: number;
  businessFiltered: number;
  excludedFiltered: number;
  existingSkipped: number;
  imported: number;
}

const BANK_KEYWORDS = [
  'BANK', 'MORTGAGE', 'CREDIT UNION', 'FEDERAL SAVINGS', 'LOAN',
  'FINANCIAL', 'LENDING', 'CAPITAL', 'FUNDING', 'FANNIE', 'FREDDIE',
  'HUD', 'VETERANS', 'FHA', 'RURAL DEVELOPMENT', 'NATIONSTAR',
  'QUICKEN', 'ROCKET', 'WELLS FARGO', 'CHASE', 'CITIMORTGAGE'
];

const BUSINESS_KEYWORDS = [
  'LLC', 'INC', 'CORP', 'COMPANY', 'LTD', 'HOLDINGS', 'PROPERTIES',
  'ENTERPRISES', 'ASSOCIATES', 'GROUP', 'PARTNERS', 'DEVELOPMENT',
  'INVESTMENTS', 'REALTY', 'REAL ESTATE', 'CONSTRUCTION', 'BUILDERS',
  'SERVICES', 'MANAGEMENT', 'SOLUTIONS'
];

const EXCLUDED_KEYWORDS = ['CHURCH', 'PARISH', 'MINISTRY', 'MINISTRIES', 'CONGREGATION', 'IRA'];

const TRUST_KEYWORDS = ['TRUST', 'TRUSTEE', 'REVOCABLE', 'IRREVOCABLE', 'LIVING TRUST'];

const extractLastName = (name: string): string => {
  const cleaned = name.trim().toUpperCase();
  const parts = cleaned.split(/\s+/);
  return parts[0];
};

const isExcludedEntity = (name: string): boolean => {
  const upper = name.toUpperCase();
  return EXCLUDED_KEYWORDS.some(kw => upper.includes(kw));
};

const isBusinessEntity = (name: string): boolean => {
  const upper = name.toUpperCase();
  return BUSINESS_KEYWORDS.some(kw => upper.includes(kw)) ||
         TRUST_KEYWORDS.some(kw => upper.includes(kw));
};

const isBankEntity = (name: string): boolean => {
  const upper = name.toUpperCase();
  return BANK_KEYWORDS.some(kw => upper.includes(kw));
};

const isFamilyTransfer = (party1: string, party2: string): boolean => {
  if (isBusinessEntity(party1) || isBankEntity(party1)) return false;
  if (isBusinessEntity(party2) || isBankEntity(party2)) return false;
  const last1 = extractLastName(party1);
  const last2 = extractLastName(party2);
  return last1 === last2 && last1.length > 1;
};

const isBusinessToBusinessTransfer = (party1: string, party2: string): boolean => {
  return (isBusinessEntity(party1) || isBankEntity(party1)) &&
         (isBusinessEntity(party2) || isBankEntity(party2));
};

// Split the concatenated names+legal portion into party1, party2, legal
// Legal starts with patterns like "S:", "Sub:", "Multiple", "Lot:"
// Names are ALL CAPS words
const splitNamesAndLegal = (text: string): { party1: string; party2: string; legal: string } => {
  // First, split off legal description
  // Legal patterns: "S: \d", "Sub: ", "Multiple", "Lot: \d"
  const legalPatterns = [
    /S:\s*\d/,
    /Sub:\s*/,
    /Multiple\s/,
    /Lot:\s*\d/,
    /GovtLot:\s*\d/,
  ];

  let namesPart = text;
  let legal = '';

  for (const pattern of legalPatterns) {
    const match = text.match(pattern);
    if (match && match.index !== undefined) {
      // Use the earliest match
      if (!legal || match.index < text.indexOf(legal)) {
        namesPart = text.substring(0, match.index).trim();
        legal = text.substring(match.index).trim();
      }
    }
  }

  // If no legal found, try from the end
  if (!legal) {
    // Check if the entire text might just be names with no legal
    namesPart = text;
  }

  // Now split namesPart into party1 and party2
  // If there are tabs, use those
  if (namesPart.includes('\t')) {
    const parts = namesPart.split('\t').filter(Boolean);
    return {
      party1: parts[0]?.trim() || '',
      party2: parts.slice(1).join(' ').trim(),
      legal,
    };
  }

  // If there are multiple consecutive spaces (2+), use those as delimiter
  const spaceParts = namesPart.split(/\s{2,}/).filter(Boolean);
  if (spaceParts.length >= 2) {
    return {
      party1: spaceParts[0].trim(),
      party2: spaceParts.slice(1).join(' ').trim(),
      legal,
    };
  }

  // No clear separator — try to find the boundary between two ALL-CAPS names
  // Strategy: the boundary is where one name segment ends and another begins
  // We'll look for common patterns where a name ends:
  // - Single letter (middle initial) followed by a different name pattern
  // - Known suffixes (JR, SR, II, III, IV) followed by a new name
  
  // Try to find known entity boundaries (LLC, INC, TRUST, etc.)
  const entityEndPatterns = [
    /\b(LLC|INC|CORPORATION|COMPANY|LTD)\b/gi,
    /\b(TRUST)\b/gi,
  ];

  for (const pattern of entityEndPatterns) {
    const matches = [...namesPart.matchAll(pattern)];
    if (matches.length === 1) {
      const m = matches[0];
      const endIdx = m.index! + m[0].length;
      // Check if this entity keyword is near the beginning or middle
      if (endIdx < namesPart.length - 2) {
        return {
          party1: namesPart.substring(0, endIdx).trim(),
          party2: namesPart.substring(endIdx).trim(),
          legal,
        };
      }
    }
  }

  // Last resort: try to split individual person names
  // Look for a pattern where a single letter (middle initial or suffix) is followed
  // by an uppercase name that starts a new person
  // Pattern: "LASTNAME FIRSTNAME M" then "LASTNAME2 FIRSTNAME2"
  // The boundary is after a single uppercase letter or after JR/SR/II/III
  const suffixSplit = namesPart.match(
    /^(.+?\b(?:[A-Z]|JR|SR|II|III|IV)\b)\s*([A-Z][A-Z]+\s.+)$/
  );
  if (suffixSplit) {
    return {
      party1: suffixSplit[1].trim(),
      party2: suffixSplit[2].trim(),
      legal,
    };
  }

  // If we truly can't split, put everything as party1
  return { party1: namesPart.trim(), party2: '', legal };
};

export function AntrimCountyTextParser({ onImportComplete }: AntrimCountyTextParserProps) {
  const [rawText, setRawText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const { toast } = useToast();

  const parseAntrimText = (text: string): ParsedRecord[] => {
    const records: ParsedRecord[] = [];
    const lines = text.split('\n');

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      // Match: {docNumber}LAND CONTRACT MEMORANDUM{date} {time} {AM|PM}{remainder}
      // Key insight: AM/PM runs directly into the party name with no space
      // e.g., "11:23:50 AMPULFORD TERRY..." or "1:23:46 PMZAKA FAMILY TRUST..."
      const match = trimmed.match(
        /^(\d{10,15})LAND CONTRACT MEMORANDUM\s*(\d{1,2}\/\d{1,2}\/\d{4})\s+(\d{1,2}:\d{2}:\d{2})\s+(AM|PM)(.+)$/i
      );

      if (!match) continue;

      const documentNumber = match[1];
      const dateStr = match[2];
      const ampm = match[4];
      const remainder = match[5].trim();

      // Parse date to YYYY-MM-DD
      const dateParts = dateStr.split('/');
      const filingDate = dateParts.length === 3
        ? `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`
        : dateStr;

      // Split remainder into party1, party2, legal
      const { party1, party2, legal } = splitNamesAndLegal(remainder);

      if (party1 && documentNumber) {
        records.push({
          documentNumber,
          documentType: 'LAND CONTRACT MEMORANDUM',
          filingDate,
          party1,
          party2,
          legalDescription: legal,
        });
      }
    }

    console.log(`Antrim parser: found ${records.length} records`);
    return records;
  };

  const processRecords = async () => {
    if (!rawText.trim()) {
      toast({ title: "No data", description: "Please paste Antrim County data to import", variant: "destructive" });
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);

    try {
      const allRecords = parseAntrimText(rawText);
      setProgress(20);

      if (allRecords.length === 0) {
        toast({ 
          title: "No records found", 
          description: "Could not parse any records from the pasted text. Make sure you're pasting rows from the Antrim County search results.", 
          variant: "destructive" 
        });
        setIsProcessing(false);
        return;
      }

      const result: ImportResult = {
        totalParsed: allRecords.length,
        familyFiltered: 0,
        bankFiltered: 0,
        businessFiltered: 0,
        excludedFiltered: 0,
        existingSkipped: 0,
        imported: 0,
      };

      // Apply filters
      const validRecords: ParsedRecord[] = [];
      for (const rec of allRecords) {
        if (isExcludedEntity(rec.party1) || isExcludedEntity(rec.party2)) {
          result.excludedFiltered++;
          continue;
        }
        if (isBankEntity(rec.party1) || isBankEntity(rec.party2)) {
          result.bankFiltered++;
          continue;
        }
        if (rec.party2 && isBusinessToBusinessTransfer(rec.party1, rec.party2)) {
          result.businessFiltered++;
          continue;
        }
        if (rec.party2 && isFamilyTransfer(rec.party1, rec.party2)) {
          result.familyFiltered++;
          continue;
        }
        validRecords.push(rec);
      }
      setProgress(50);

      // Check existing
      const docNumbers = validRecords.map(r => r.documentNumber);
      const { data: existing } = await supabase
        .from('county_research')
        .select('document_number')
        .eq('county', 'antrim')
        .in('document_number', docNumbers.length > 0 ? docNumbers : ['__none__']);

      const existingSet = new Set(existing?.map(e => e.document_number) || []);
      setProgress(60);

      const recordsToInsert = validRecords.filter(r => {
        if (existingSet.has(r.documentNumber)) {
          result.existingSkipped++;
          return false;
        }
        return true;
      });

      if (recordsToInsert.length > 0) {
        const insertData = recordsToInsert.map(rec => ({
          county: 'antrim',
          document_number: rec.documentNumber,
          document_type: rec.documentType,
          filing_date: rec.filingDate,
          grantor_name: rec.party1,
          grantee_name: rec.party2 || null,
          legal_description: rec.legalDescription || null,
          is_potential_owner_finance: true,
          confidence_score: 85,
          detection_reasons: JSON.stringify(['Land Contract Memorandum', 'Antrim County MI']),
          search_date: new Date().toISOString(),
          status: 'new',
        }));

        const batchSize = 50;
        for (let i = 0; i < insertData.length; i += batchSize) {
          const batch = insertData.slice(i, i + batchSize);
          const { error } = await supabase.from('county_research').insert(batch);
          if (error) {
            console.error('Insert error:', error);
            throw error;
          }
          result.imported += batch.length;
          setProgress(60 + ((i + batch.length) / insertData.length) * 35);
        }
        backupRecordsToExcel(insertData, 'antrim', 'antrim_parser');
      }

      setProgress(100);
      setImportResult(result);
      setRawText("");

      toast({
        title: "Import complete",
        description: `Imported ${result.imported} records from Antrim County`,
      });

      if (onImportComplete) onImportComplete();
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Import failed",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setRawText(text);
    toast({ title: "File loaded", description: `Loaded ${file.name} - ${text.split('\n').length} lines` });
  };

  // Preview parsing to help debug
  const previewParse = () => {
    const records = parseAntrimText(rawText);
    console.log('Preview parse results:', records);
    toast({
      title: `Preview: ${records.length} records found`,
      description: records.length > 0 
        ? `First: ${records[0].party1} → ${records[0].party2 || '(no buyer)'}`
        : 'No records could be parsed. Check console for details.',
    });
  };

  return (
    <div className="space-y-4">
      {importResult && (
        <Alert className={importResult.imported > 0 ? "border-green-500" : "border-yellow-500"}>
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Import Results</AlertTitle>
          <AlertDescription>
            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
              <div>Total Records Parsed: <strong>{importResult.totalParsed}</strong></div>
              <div>Family Transfers Filtered: <strong>{importResult.familyFiltered}</strong></div>
              <div>Bank Transactions Filtered: <strong>{importResult.bankFiltered}</strong></div>
              <div>Business-to-Business Filtered: <strong>{importResult.businessFiltered}</strong></div>
              <div>Church/IRA Filtered: <strong>{importResult.excludedFiltered}</strong></div>
              <div>Already in Database: <strong>{importResult.existingSkipped}</strong></div>
              <div className="text-green-600">Successfully Imported: <strong>{importResult.imported}</strong></div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Antrim County, MI - Land Contract Parser
          </CardTitle>
          <CardDescription>
            Paste search results from Antrim County Register of Deeds website.
            Party 1 = Seller (Grantor), Party 2 = Buyer (Grantee).
            Automatically filters family transfers, bank transactions, and business-to-business deals.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <label className="cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Upload Text File
                <input type="file" accept=".txt" onChange={handleFileUpload} className="hidden" />
              </label>
            </Button>
            <Button variant="outline" onClick={() => setRawText("")} disabled={!rawText}>
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
            <Button variant="secondary" onClick={previewParse} disabled={!rawText.trim()}>
              Preview Parse
            </Button>
          </div>

          <Textarea
            placeholder={`Paste Antrim County search results here...

Copy all rows from the search results table. Each line should contain:
Document No + Document Type + Recorded Date + Party1 + Party2 + Legals

Example:
202100011637LAND CONTRACT MEMORANDUM12/13/2021 10:36:52 AMWITTBRODT KATHY MFERNHAUS INN LLCSub: HH NOBLES ADD...`}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            className="min-h-[300px] font-mono text-xs"
          />

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">
                Processing... {Math.round(progress)}%
              </p>
            </div>
          )}

          <Button onClick={processRecords} disabled={isProcessing || !rawText.trim()} className="w-full">
            {isProcessing ? "Processing..." : "Parse & Import Records"}
          </Button>
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Filtering Rules</AlertTitle>
        <AlertDescription className="text-xs space-y-1">
          <p>• <strong>Party 1</strong> = Seller/Grantor (the person we want to contact)</p>
          <p>• <strong>Party 2</strong> = Buyer/Grantee</p>
          <p>• <strong>Family transfers</strong>: Same last name on both sides → filtered out</p>
          <p>• <strong>Business-to-business</strong>: Both parties are entities → filtered out</p>
          <p>• <strong>Bank transactions</strong>: Any party is a bank/mortgage company → filtered out</p>
          <p>• <strong>Church/IRA</strong>: Any party is a church or IRA → filtered out</p>
          <p>• <strong>Duplicates</strong>: Existing document numbers in database are skipped</p>
        </AlertDescription>
      </Alert>
    </div>
  );
}
